import React, {useEffect, useState} from 'react'
import {Modal, Button, Table, Spinner} from 'react-bootstrap'

import {
  JobseekerRegistrationService,
  JobseekerRegistration,
} from 'app/services/JobseekerRegistrationService'
import saveAs from 'file-saver'
import moment from 'moment'

interface JobseekerRegistrationsModalProps {
  show: boolean
  onHide: () => void
  organizationId: string
}

// Use the type from the service

const JobseekerRegistrationsModal: React.FC<JobseekerRegistrationsModalProps> = ({
  show,
  onHide,
  organizationId,
}) => {
  const [jobseekers, setJobseekers] = useState<JobseekerRegistration[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState<boolean>(false)

  useEffect(() => {
    if (show && organizationId) {
      setLoading(true)
      setError(null)
      const service = new JobseekerRegistrationService()
      service
        .getAllByOrganization(organizationId)
        .then((data: JobseekerRegistration[]) => {
          setJobseekers(data)
        })
        .catch(() => {
          setError('Failed to load jobseekers.')
        })
        .finally(() => setLoading(false))
    }
  }, [show, organizationId])

  const handleDownloadExcel = async () => {
    setDownloading(true)
    try {
      const service = new JobseekerRegistrationService()
      const base64Data = await service.getExcelDownload(organizationId)
      if (base64Data) {
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        saveAs(blob, `jobseeker-registrations-${moment().format('yyyy-MM-DD')}.xlsx`)
      }
    } catch (error) {
      console.error('Error downloading excel:', error)
      alert('Failed to download excel.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <div className='d-flex justify-content-between align-items-center w-100'>
          <Modal.Title>Registered Jobseekers</Modal.Title>
          {jobseekers.length > 0 && (
            <Button
              variant='primary'
              size='sm'
              onClick={handleDownloadExcel}
              disabled={downloading}
              className='me-3'
            >
              {downloading ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' /> Downloading...
                </>
              ) : (
                'Download Excel'
              )}
            </Button>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='text-center'>
            <Spinner animation='border' />
          </div>
        ) : error ? (
          <div className='text-danger'>{error}</div>
        ) : jobseekers.length === 0 ? (
          <div>No jobseekers registered yet.</div>
        ) : (
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Registration Date</th>
              </tr>
            </thead>
            <tbody>
              {jobseekers.map((jobseeker) => (
                <tr key={jobseeker.id}>
                  <td>{jobseeker.given_name}</td>
                  <td>{jobseeker.family_name}</td>
                  <td>{jobseeker.email}</td>
                  <td>{jobseeker.phone_number}</td>
                  <td>
                    {jobseeker.createdAt
                      ? moment(jobseeker.createdAt).format('YYYY-MM-DD HH:mm')
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default JobseekerRegistrationsModal
